// Katalog satırının paket rozetleri (spec §5 Katalog, DR12/DR15): en çok iki kısa ad (mavi pill,
// tam ad `title`'da) + "+N" — 44 px dokunma hedefli düğme, tam liste açılır panelde (`aria-label`);
// Esc ve dışarı tıklama kapatır, Esc odağı düğmeye döndürür. Satır tıklaması düzenleme penceresini
// açtığı için tıklama/tuş olayları satıra yayılmaz.

import { useEffect, useId, useRef, useState } from "react";
import type { PackageBadges } from "../../lib/kpi-catalog-packages";
import { KPI_PACKAGES_COPY } from "../../lib/kpi-packages-copy";
import { hitArea44, pillStyle } from "./kpi-form-styles";

const C = KPI_PACKAGES_COPY.catalog;

/** Görünmez ama erişilebilirlik adına katılan metin (TODO-UX-29/2: aria-label + görünür çift okuma yok). */
const SR_ONLY: React.CSSProperties = {
  position: "absolute",
  width: 1,
  height: 1,
  overflow: "hidden",
  clip: "rect(0 0 0 0)",
  whiteSpace: "nowrap",
};

export function CatalogPackageBadges(props: {
  badges: PackageBadges;
  metricLabel: string;
}): JSX.Element | null {
  const { badges, metricLabel } = props;
  const [open, setOpen] = useState(false);
  const listId = useId();
  const pillId = `${listId}-pill`;
  const nameId = `${listId}-name`;
  const rootRef = useRef<HTMLSpanElement>(null);
  const btnRef = useRef<HTMLButtonElement>(null);

  useEffect(() => {
    if (!open) return;
    function onDown(e: MouseEvent): void {
      if (rootRef.current && !rootRef.current.contains(e.target as Node)) setOpen(false);
    }
    function onKey(e: KeyboardEvent): void {
      if (e.key === "Escape") {
        e.preventDefault();
        setOpen(false);
        btnRef.current?.focus();
      }
    }
    document.addEventListener("mousedown", onDown);
    document.addEventListener("keydown", onKey);
    return () => {
      document.removeEventListener("mousedown", onDown);
      document.removeEventListener("keydown", onKey);
    };
  }, [open]);

  if (badges.all.length === 0) return null;
  return (
    <span
      ref={rootRef}
      className="inline-flex items-center gap-1 flex-wrap"
      data-testid="kpi-catalog-pkg-badges"
      style={{ position: "relative" }}
      onClick={(e) => e.stopPropagation()}
      onKeyDown={(e) => e.stopPropagation()}
    >
      {badges.shown.map((b) => (
        <span key={b.id} style={pillStyle} title={b.name}>
          {b.short}
        </span>
      ))}
      {badges.rest > 0 ? (
        <>
          <button
            ref={btnRef}
            type="button"
            aria-labelledby={`${pillId} ${nameId}`}
            aria-expanded={open}
            aria-controls={open ? listId : undefined}
            onClick={() => setOpen((o) => !o)}
            style={{ ...hitArea44, margin: "-12px -6px" }}
          >
            <span id={pillId} style={pillStyle}>
              {C.more(badges.rest)}
            </span>
            <span id={nameId} style={SR_ONLY}>
              {C.listLabel(metricLabel)}
            </span>
          </button>
          {open ? (
            <ul
              id={listId}
              aria-label={C.listLabel(metricLabel)}
              style={{
                position: "absolute",
                zIndex: 30,
                top: "calc(100% + 4px)",
                left: 0,
                minWidth: 200,
                margin: 0,
                padding: 6,
                listStyle: "none",
                background: "var(--color-surface)",
                border: "1px solid var(--color-border)",
                borderRadius: 8,
                boxShadow: "0 8px 24px rgba(0,0,0,0.08)",
              }}
            >
              {badges.all.map((b) => (
                <li
                  key={b.id}
                  style={{ padding: "4px 6px", fontSize: "0.85rem", color: "var(--color-text)" }}
                >
                  {b.name}
                </li>
              ))}
            </ul>
          ) : null}
        </>
      ) : null}
    </span>
  );
}
